import {Input} from '@mantine/core';
import {Space} from 'src/design/atoms/Space';
import React, {useCallback} from 'react';
import {AssetTreeSelector} from 'src/app/assets/AssetTreeSelector';
import {formsAssetsKey} from 'src/app/forms/offline/snapshotKeys';
import {useOfflineSectionAvailable} from 'src/app/forms/offline/useOfflineSectionAvailable';
import {HelpText} from 'src/design/atoms/HelpText';
import type {IFormFieldV2} from 'src/types/FormV2';

export interface FormAssetSelectorV2Props {
  field: IFormFieldV2;
  value: string | number;
  error?: string;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

const OFFLINE_NOTICE =
  'Offline — the asset list is not cached on this device for the current ' +
  'project. Reconnect to select an asset.';

export const FormAssetSelectorV2 = React.memo<FormAssetSelectorV2Props>(
  ({field, value, error, onChange}: FormAssetSelectorV2Props) => {
    const unavailable = useOfflineSectionAvailable(formsAssetsKey) === false;
    const handleAssetChange = useCallback(
      (id?: number | undefined, text?: string): void => {
        onChange(field.name, id, text);
      },
      [field.name, onChange]
    );

    return (
      <Input.Wrapper
        data-testid={`form-field-${field.name}`}
        label={field.label}
        withAsterisk={field.required}
        error={error}
        description={field.description}>
        <Space mb="xs" />
        <AssetTreeSelector
          id={value ? Number(value) : undefined}
          onChange={handleAssetChange}
          includeLeaves={field.assetOptions?.includeLeaves ?? true}
          requireLeaf={field.assetOptions?.requireLeaf ?? true}
          rootAssetId={field.assetOptions?.rootAssetId}
          disabled={field.disabled || unavailable}
          showFullPath={field.assetOptions?.showFullPath ?? true}
        />
        {unavailable ? <HelpText text={OFFLINE_NOTICE} /> : null}
        <Space mb="xs" />
      </Input.Wrapper>
    );
  }
);
